import { useLikedBrandIds } from '@aboutyou/legacy-shared'
import { SubmitBirthdayMissionType } from '@aboutyou/legacy-shared/src/types/missions'
import { splitEvery } from 'helpers/splitEvery'
import { useTrackOriginClick } from 'helpers/tracking/hooks/trackOriginClick'
import React, { FC, memo, useCallback } from 'react'
import {
  IBrandItem,
  IBrandsGroup,
} from 'store/entities/Brands/Brand.normalizer'
import styled from 'styled-components'
import { outline } from 'uiComponents/constants/colors'
import { mediaDesktopAndUp } from 'uiComponents/constants/customMedia'
import { fsXXL } from 'uiComponents/constants/typography'
import Brand from '../Brand'

const BrandsColumns: FC<{
  brands: IBrandItem[]
  submitLikeBrandMission?: SubmitBirthdayMissionType
}> = ({ brands, submitLikeBrandMission }) => {
  const columns = splitEvery(Math.ceil(brands.length / 2), brands)

  const trackOriginClick = useTrackOriginClick()

  const likedBrandIds = useLikedBrandIds()

  const handleBrandClick = useCallback(
    (brandId: string) => {
      trackOriginClick('brands_list')

      // at this point the brand is already liked, so we need to submit the mission
      if (likedBrandIds.includes(Number(brandId))) {
        submitLikeBrandMission?.(+brandId)
      }
    },
    [trackOriginClick, likedBrandIds, submitLikeBrandMission],
  )

  return (
    <>
      {columns.map((column, i) => {
        return (
          <BrandColumn key={i}>
            {column.map(brand => (
              <Brand
                key={brand.id}
                brand={brand}
                isLiked={likedBrandIds.includes(Number(brand.id))}
                onClick={() => handleBrandClick(brand.id)}
              />
            ))}
          </BrandColumn>
        )
      })}
    </>
  )
}

const BrandColumn = styled.div`
  flex: 0 1 50%;
  max-width: 50%;
  position: relative;

  &::before {
    display: block;
    background-color: ${outline};
    width: 1px;
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
  }

  &:first-child {
    padding-right: 12px;

    @media ${mediaDesktopAndUp} {
      padding-right: 42px;
    }

    &::before {
      display: none;
    }
  }

  &:last-child {
    padding-left: 13px;

    @media ${mediaDesktopAndUp} {
      padding-left: 43px;
    }
  }
`

const BrandsSection: FC<{
  brandsGroup: IBrandsGroup
  submitLikeBrandMission?: SubmitBirthdayMissionType
}> = ({ brandsGroup: brandsSection, submitLikeBrandMission }) => {
  const { id, brands } = brandsSection

  return (
    <SectionContainer data-testid="BrandSection">
      <SectionLetter>{id}</SectionLetter>
      <SectionBrands>
        <BrandsColumns
          brands={brands}
          submitLikeBrandMission={submitLikeBrandMission}
        />
      </SectionBrands>
    </SectionContainer>
  )
}

const MemoBrandsSection = memo(BrandsSection)

const SectionContainer = styled.div`
  position: relative;
  padding: 30px 0 30px 45px;
  min-height: 100px;
`

const SectionLetter = styled.strong`
  font-size: ${fsXXL}px;
  font-weight: 300;
  position: absolute;
  top: 21px;
  left: 0;
  text-transform: uppercase;
`

const SectionBrands = styled.div`
  display: flex;
  margin-top: -3px;
`

export default MemoBrandsSection
